import { ChartResult, formatMeasure } from '@employ-engage/data';
import { useTranslation } from 'react-i18next';
import { StatDelta } from './stat-delta';

/**
 * One number, large, with what it is underneath.
 *
 * A scored measure with nothing to break it down by is not a chart — it is a
 * figure. This lived only inside the report builder's card, so the dashboard
 * had no way to draw a pinned one: `'stat'` is not in `ChartVizType`, the tile
 * fell through to a bar chart with no rows, and `Chart` short-circuits that to
 * "No responses in this range yet". The card showed 63% and the tile said
 * there was no data.
 *
 * Shared so that stops being possible, rather than being fixed twice.
 */

export type StatMeasure = 'topBox' | 'bottomBox' | 'mean';

export interface StatFigureProps {
  result?: ChartResult | null;
  measure: StatMeasure;
  /** The top of the scale, for the caption under an average. */
  scaleMax?: number;
  className?: string;
}

/** The one figure a scored measure produces, formatted for its kind. */
export function formatStat(
  result: ChartResult | null | undefined,
  measure: StatMeasure
) {
  // An average of an ordinal scale does not support more than one decimal,
  // which is `formatMeasure`'s whole distinction.
  return formatMeasure(result?.cells?.[0]?.value, measure);
}

export function StatFigure({
  result,
  measure,
  scaleMax = 5,
  className,
}: StatFigureProps) {
  const { t } = useTranslation();

  // Withheld is not the same as zero, and must never fall through to a number.
  if (result?.meta?.suppressedAll) {
    return (
      <div
        className={`flex h-full flex-col items-center justify-center ${
          className ?? ''
        }`}
      >
        <span className="text-sm text-light-secondaryText">
          {t('Not enough responses yet to show this safely.')}
        </span>
      </div>
    );
  }

  const answered = result?.cells?.[0]?.n ?? 0;

  return (
    <div
      className={`flex h-full flex-col items-center justify-center ${
        className ?? ''
      }`}
    >
      <span className="text-5xl font-semibold text-light-primary">
        {formatStat(result, measure)}
      </span>
      <span className="mt-2 text-center text-xs text-light-secondaryText dark:text-dark-secondaryText">
        {measure === 'mean'
          ? t('out of {{max}} — {{count}} answered', {
              max: scaleMax,
              count: answered,
            })
          : measure === 'bottomBox'
          ? t('or worse — {{count}} answered', { count: answered })
          : t('agree or better — {{count}} answered', { count: answered })}
      </span>
      {/* An average moves in scale points; a box measure in percentage
          points. Saying "percentage points" under a 4.2 would be wrong. */}
      <StatDelta
        result={result}
        units={measure === 'mean' ? 'points' : 'percentagePoints'}
      />
    </div>
  );
}

export default StatFigure;
